import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Flex,
    Skeleton,
    Text,
} from '@chakra-ui/react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { chartColorAt } from '../../../ui';
import type {
    InventoryActivitySegment,
    InventoryMetricAggregates,
    InventoryMetricsTimeseriesResponse,
    InventoryReportingTimeseriesDataPoint,
    InventorySampleType,
    ReportingComparison,
} from '../../../types/reporting';
import {
    formatChartValue,
    formatCompactCurrency,
    formatCompactNumber,
    type MetricFormat,
    PRIOR_COMPARISON_BAR_FILL,
    TOOLTIP_STYLE,
} from '../shared/formatters';
import { formatShortDate } from '../shared/dateUtils';
import { ReportingDonut } from '../shared/ReportingDonut';
import {
    aggregateTotal,
    inventoryMetricNeedsSampleType,
    resolveInventoryMetricValue,
} from '../widgets/inventoryMetricResolve';

type FlowUnit = 'quantity' | 'value';
type BusinessLineMetric = 'usedQuantity' | 'usedSoldValue' | 'salesValue';

export interface InventoryTimeseriesChartProps {
    data?: InventoryMetricsTimeseriesResponse;
    isLoading: boolean;
    metric: string;
    chartStyle?: 'bars' | 'stacked' | 'line' | 'donut' | 'histogram';
    comparison?: ReportingComparison;
    showComparisonBars?: boolean;
    showTargetReorderRefs?: boolean;
    height?: string | number;
    granularity?: string;
    preset?: string;
    sampleType?: InventorySampleType;
    onSampleTypeChange?: (next: InventorySampleType) => void;
}

const formatAxis = (v: number, currency: boolean) =>
    currency ? formatCompactCurrency(v) : formatCompactNumber(v);

const signedBarColor = (v: number) => (v >= 0 ? '#27AE60' : '#EB5757');

const ZERO_AGG: InventoryMetricAggregates = {
    total: 0,
    average: { perUnit: 0, perItem: 0 },
    median: { perUnit: 0, perItem: 0 },
};

function emptySegment(): InventoryActivitySegment {
    return {
        usedQuantity: ZERO_AGG,
        usedValue: ZERO_AGG,
        soldQuantity: ZERO_AGG,
        salesValue: ZERO_AGG,
        orderedQuantity: ZERO_AGG,
        orderedValue: ZERO_AGG,
        initialQuantity: ZERO_AGG,
        initialValue: ZERO_AGG,
    };
}

function sumSegments(
    points: InventoryReportingTimeseriesDataPoint[],
    pick: (p: InventoryReportingTimeseriesDataPoint) => Record<string, InventoryActivitySegment>,
): Array<{ name: string; value: number; color: string }> {
    const totals = new Map<string, number>();
    for (const p of points) {
        const segments = pick(p);
        for (const [key, seg] of Object.entries(segments)) {
            const add =
                aggregateTotal(seg.usedQuantity)
                + aggregateTotal(seg.soldQuantity)
                + aggregateTotal(seg.orderedQuantity)
                + aggregateTotal(seg.initialQuantity);
            if (
                add === 0
                && aggregateTotal(seg.usedValue) === 0
                && aggregateTotal(seg.salesValue) === 0
            ) {
                continue;
            }
            totals.set(key, (totals.get(key) ?? 0) + add);
        }
    }
    return Array.from(totals.entries())
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value], i) => ({
            name: name === 'unknown' ? 'Unknown' : name,
            value,
            rawValue: value,
            color: chartColorAt(i),
        }));
}

function businessLineValue(seg: InventoryActivitySegment, metric: BusinessLineMetric): number {
    if (metric === 'usedQuantity') return aggregateTotal(seg.usedQuantity);
    if (metric === 'salesValue') return aggregateTotal(seg.salesValue);
    return aggregateTotal(seg.usedValue) + aggregateTotal(seg.salesValue);
}

function isCurrencyMetric(metric: string, flowUnit: FlowUnit, blMetric: BusinessLineMetric): boolean {
    if (metric === 'quantityFlow') return flowUnit === 'value';
    if (metric === 'activityByBusinessLine') return blMetric !== 'usedQuantity';
    return (
        metric.includes('Value')
        || metric.includes('Sales')
        || metric === 'inStockValue'
        || metric === 'avgInStockValue'
        || metric === 'medianInStockValue'
        || metric === 'salesValue'
        || metric === 'avgSalesValue'
        || metric === 'medianSalesValue'
        || metric === 'netTransactionValue'
        || metric.endsWith('Value')
    );
}

function isInStockMetric(metric: string): boolean {
    return (
        metric === 'inStockQuantity'
        || metric === 'avgInStockQuantity'
        || metric === 'medianInStockQuantity'
    );
}

function InventoryTimeseriesChart({
    data,
    isLoading,
    metric,
    chartStyle = 'bars',
    comparison,
    showComparisonBars = false,
    showTargetReorderRefs = false,
    height = '100%',
    granularity,
    preset,
    sampleType = 'perUnit',
    onSampleTypeChange,
}: InventoryTimeseriesChartProps) {
    const [flowUnit, setFlowUnit] = useState<FlowUnit>('quantity');
    const [blMetric, setBlMetric] = useState<BusinessLineMetric>('usedQuantity');
    const [localSampleType, setLocalSampleType] = useState<InventorySampleType>('perUnit');

    const effectiveSampleType = onSampleTypeChange ? sampleType : localSampleType;
    const setSampleType = onSampleTypeChange ?? setLocalSampleType;
    const showSampleType = inventoryMetricNeedsSampleType(metric);

    const points = useMemo(() => data?.data ?? [], [data?.data]);
    const priorPoints = useMemo(
        () => (comparison ? (data?.priorData ?? []) : []),
        [comparison, data?.priorData],
    );
    const isCurrency = isCurrencyMetric(metric, flowUnit, blMetric);

    const quantityFlowRows = useMemo(() => {
        return points.map((p) => {
            if (flowUnit === 'quantity') {
                const ordered = aggregateTotal(p.orderedQuantity);
                const initial = aggregateTotal(p.initialQuantity);
                const used = aggregateTotal(p.usedQuantity);
                const sold = aggregateTotal(p.soldQuantity);
                const inbound = aggregateTotal(p.inboundQuantity) || ordered + initial;
                const outbound = aggregateTotal(p.outboundQuantity) || used + sold;
                const priorInbound =
                    aggregateTotal(p.prior?.inboundQuantity)
                    || (
                        aggregateTotal(p.prior?.orderedQuantity)
                        + aggregateTotal(p.prior?.initialQuantity)
                    );
                const priorOutbound =
                    aggregateTotal(p.prior?.outboundQuantity)
                    || (
                        aggregateTotal(p.prior?.usedQuantity)
                        + aggregateTotal(p.prior?.soldQuantity)
                    );
                return {
                    date: p.date,
                    label: formatShortDate(p.date),
                    ordered,
                    initial,
                    used: -used,
                    sold: -sold,
                    net: inbound - outbound,
                    priorNet: p.prior ? priorInbound - priorOutbound : undefined,
                };
            }
            const ordered = aggregateTotal(p.orderedValue);
            const initial = aggregateTotal(p.initialValue);
            const used = aggregateTotal(p.usedValue);
            const sold = aggregateTotal(p.salesValue);
            const inbound = aggregateTotal(p.inboundValue) || ordered + initial;
            const outbound = aggregateTotal(p.outboundValue) || used + sold;
            const priorInbound =
                aggregateTotal(p.prior?.inboundValue)
                || (
                    aggregateTotal(p.prior?.orderedValue)
                    + aggregateTotal(p.prior?.initialValue)
                );
            const priorOutbound =
                aggregateTotal(p.prior?.outboundValue)
                || (
                    aggregateTotal(p.prior?.usedValue)
                    + aggregateTotal(p.prior?.salesValue)
                );
            return {
                date: p.date,
                label: formatShortDate(p.date),
                ordered,
                initial,
                used: -used,
                sold: -sold,
                net: inbound - outbound,
                priorNet: p.prior ? priorInbound - priorOutbound : undefined,
            };
        });
    }, [points, flowUnit]);

    const simpleRows = useMemo(() => {
        return points.map((p) => ({
            date: p.date,
            label: formatShortDate(p.date),
            value: resolveInventoryMetricValue(p, metric, effectiveSampleType) ?? 0,
            prior: p.prior
                ? resolveInventoryMetricValue(p.prior, metric, effectiveSampleType)
                : undefined,
        }));
    }, [points, metric, effectiveSampleType]);

    const multiLineRows = useMemo(() => {
        return points.map((p) => ({
            date: p.date,
            label: formatShortDate(p.date),
            ordered: aggregateTotal(p.orderedQuantity),
            used: aggregateTotal(p.usedQuantity),
            sold: aggregateTotal(p.soldQuantity),
        }));
    }, [points]);

    const locationRows = useMemo(() => {
        const locs = data?.locations ?? [];
        return points.map((p) => {
            const row: Record<string, string | number> = {
                date: p.date,
                label: formatShortDate(p.date),
            };
            for (const loc of locs) {
                const seg = p.byLocation?.[loc.id];
                row[loc.id] =
                    aggregateTotal(seg?.orderedQuantity)
                    + aggregateTotal(seg?.initialQuantity)
                    + aggregateTotal(seg?.usedQuantity)
                    + aggregateTotal(seg?.soldQuantity);
            }
            return row;
        });
    }, [points, data?.locations]);

    const donutSlices = useMemo(() => {
        const categoryActivity = (seg: InventoryActivitySegment) =>
            aggregateTotal(seg.usedQuantity)
            + aggregateTotal(seg.soldQuantity)
            + aggregateTotal(seg.orderedQuantity)
            + aggregateTotal(seg.initialQuantity);

        if (metric === 'activityByCategory') {
            const totals = { part: 0, material: 0, liquid: 0 };
            const priorTotals = { part: 0, material: 0, liquid: 0 };
            for (const p of points) {
                for (const key of ['part', 'material', 'liquid'] as const) {
                    totals[key] += categoryActivity(p.byCategory?.[key] ?? emptySegment());
                }
            }
            for (const p of priorPoints) {
                for (const key of ['part', 'material', 'liquid'] as const) {
                    priorTotals[key] += categoryActivity(p.byCategory?.[key] ?? emptySegment());
                }
            }
            return (['part', 'material', 'liquid'] as const)
                .filter((k) => totals[k] > 0 || (comparison && priorTotals[k] > 0))
                .map((k, i) => {
                    const value = totals[k];
                    return {
                        name: k.charAt(0).toUpperCase() + k.slice(1),
                        value,
                        rawValue: value,
                        priorRawValue: comparison ? priorTotals[k] : undefined,
                        color: chartColorAt(i),
                    };
                });
        }

        if (metric === 'activityByBusinessLine') {
            const totals = new Map<string, number>();
            const priorTotals = new Map<string, number>();
            for (const p of points) {
                for (const [key, seg] of Object.entries(p.byBusinessLine ?? {})) {
                    const add = businessLineValue(seg, blMetric);
                    if (add === 0) continue;
                    totals.set(key, (totals.get(key) ?? 0) + add);
                }
            }
            for (const p of priorPoints) {
                for (const [key, seg] of Object.entries(p.byBusinessLine ?? {})) {
                    const add = businessLineValue(seg, blMetric);
                    if (add === 0) continue;
                    priorTotals.set(key, (priorTotals.get(key) ?? 0) + add);
                }
            }
            const keys = new Set([
                ...Array.from(totals.keys()),
                ...Array.from(priorTotals.keys()),
            ]);
            return Array.from(keys)
                .map((key) => ({
                    key,
                    value: totals.get(key) ?? 0,
                    prior: priorTotals.get(key) ?? 0,
                }))
                .filter((row) => row.value > 0 || (comparison && row.prior > 0))
                .sort((a, b) => b.value - a.value)
                .map((row, i) => ({
                    name: row.key === 'unknown' ? 'Unknown' : row.key,
                    value: row.value,
                    rawValue: row.value,
                    priorRawValue: comparison ? row.prior : undefined,
                    color: chartColorAt(i),
                }));
        }

        return sumSegments(points, (p) => p.byBusinessLine ?? {});
    }, [points, priorPoints, metric, blMetric, comparison]);

    const sampleTypeToggle = showSampleType ? (
        <ButtonGroup size="xs" isAttached variant="outline">
            <Button
                onClick={() => setSampleType('perUnit')}
                bg={effectiveSampleType === 'perUnit' ? 'brand.50' : undefined}
                color={effectiveSampleType === 'perUnit' ? 'brand.solid' : 'fg.muted'}
            >
                Per Unit
            </Button>
            <Button
                onClick={() => setSampleType('perItem')}
                bg={effectiveSampleType === 'perItem' ? 'brand.50' : undefined}
                color={effectiveSampleType === 'perItem' ? 'brand.solid' : 'fg.muted'}
            >
                Per Item
            </Button>
        </ButtonGroup>
    ) : null;

    if (isLoading && !data) {
        return <Skeleton h={height} w="100%" borderRadius="md" />;
    }

    if (!points.length) {
        return (
            <Flex h={height} align="center" justify="center">
                <Text color="gray.400" fontSize="sm">No data for this period</Text>
            </Flex>
        );
    }

    // —— Quantity Flow ——
    if (metric === 'quantityFlow') {
        return (
            <Box h={height} w="100%" minH={0} display="flex" flexDirection="column">
                <Flex justify="flex-end" mb={2} flexShrink={0}>
                    <ButtonGroup size="xs" isAttached variant="outline">
                        <Button
                            onClick={() => setFlowUnit('quantity')}
                            bg={flowUnit === 'quantity' ? 'brand.50' : undefined}
                            color={flowUnit === 'quantity' ? 'brand.solid' : 'fg.muted'}
                        >
                            Qty
                        </Button>
                        <Button
                            onClick={() => setFlowUnit('value')}
                            bg={flowUnit === 'value' ? 'brand.50' : undefined}
                            color={flowUnit === 'value' ? 'brand.solid' : 'fg.muted'}
                        >
                            $
                        </Button>
                    </ButtonGroup>
                </Flex>
                <Box flex={1} minH={0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={quantityFlowRows} stackOffset="sign" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                            <YAxis
                                tick={{ fontSize: 11, fill: '#718096' }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => formatAxis(v, isCurrency)}
                            />
                            <Tooltip
                                contentStyle={TOOLTIP_STYLE}
                                formatter={(value: number, name: string) => [
                                    formatChartValue(Math.abs(value), isCurrency ? 'currency' : 'number'),
                                    name,
                                ]}
                            />
                            <ReferenceLine y={0} stroke="#CBD5E0" />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="ordered" name="Ordered" stackId="flow" fill={chartColorAt(0)} />
                            <Bar dataKey="initial" name="Initial" stackId="flow" fill={chartColorAt(1)} />
                            <Bar dataKey="used" name="Used" stackId="flow" fill={chartColorAt(4)} />
                            <Bar dataKey="sold" name="Sold" stackId="flow" fill={chartColorAt(3)} />
                            <Line
                                type="monotone"
                                dataKey="net"
                                name="Net"
                                stroke={chartColorAt(5)}
                                strokeWidth={2}
                                dot={false}
                            />
                            {showComparisonBars && (
                                <Line
                                    type="monotone"
                                    dataKey="priorNet"
                                    name="Prior Net"
                                    stroke="#A0AEC0"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    dot={false}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        );
    }

    // —— Ordered vs Used vs Sold ——
    if (metric === 'orderedVsUsedVsSold') {
        return (
            <Box h={height} w="100%" minH={0}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={multiLineRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                        <YAxis
                            tick={{ fontSize: 11, fill: '#718096' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => formatAxis(v, false)}
                        />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="ordered" name="Ordered" stroke={chartColorAt(0)} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="used" name="Used" stroke={chartColorAt(4)} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sold" name="Sold" stroke={chartColorAt(3)} strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    // —— Donuts (activity segment metrics only; scalar metrics never use donut) ——
    if (metric === 'activityByBusinessLine' || metric === 'activityByCategory') {
        const donutFormat: MetricFormat =
            metric === 'activityByBusinessLine'
                ? (blMetric === 'usedQuantity' ? 'number' : 'currency')
                : 'number';
        const donutMetricLabel =
            metric === 'activityByBusinessLine'
                ? (
                    blMetric === 'usedQuantity'
                        ? 'Used Qty'
                        : blMetric === 'usedSoldValue'
                            ? 'Used+Sold $'
                            : 'Sales $'
                )
                : 'Activity';

        return (
            <Box h={height} w="100%" minH={0} display="flex" flexDirection="column">
                {metric === 'activityByBusinessLine' && (
                    <Flex justify="flex-end" mb={2} flexShrink={0}>
                        <ButtonGroup size="xs" isAttached variant="outline">
                            {(
                                [
                                    ['usedQuantity', 'Used Qty'],
                                    ['usedSoldValue', 'Used+Sold $'],
                                    ['salesValue', 'Sales $'],
                                ] as const
                            ).map(([key, label]) => (
                                <Button
                                    key={key}
                                    onClick={() => setBlMetric(key)}
                                    bg={blMetric === key ? 'brand.50' : undefined}
                                    color={blMetric === key ? 'brand.solid' : 'fg.muted'}
                                >
                                    {label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </Flex>
                )}
                <Box flex={1} minH={0} minW={0} display="flex" flexDirection="column">
                    {donutSlices.length === 0 ? (
                        <Flex h="100%" align="center" justify="center">
                            <Text color="gray.400" fontSize="sm">No activity</Text>
                        </Flex>
                    ) : (
                        <ReportingDonut
                            slices={donutSlices}
                            isFullscreen={false}
                            valueFormat={donutFormat}
                            metricLabel={donutMetricLabel}
                            comparison={comparison}
                            preset={preset}
                            granularity={granularity}
                        />
                    )}
                </Box>
            </Box>
        );
    }

    // —— Activity by Location (stacked) ——
    if (metric === 'activityByLocation') {
        const locs = data?.locations ?? [];
        return (
            <Box h={height} w="100%" minH={0}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={locationRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                        <YAxis
                            tick={{ fontSize: 11, fill: '#718096' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => formatAxis(v, false)}
                        />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {locs.map((loc, i) => (
                            <Bar
                                key={loc.id}
                                dataKey={loc.id}
                                name={loc.name || 'Unknown'}
                                stackId="loc"
                                fill={chartColorAt(i)}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    // —— In-Stock line (+ refs / prior) ——
    if (isInStockMetric(metric) || chartStyle === 'line') {
        // Target/Reorder sums are totals — only coherent against total in-stock qty.
        const showRefs = metric === 'inStockQuantity' && showTargetReorderRefs;
        return (
            <Box h={height} w="100%" minH={0} display="flex" flexDirection="column">
                {sampleTypeToggle && (
                    <Flex justify="flex-end" mb={2} flexShrink={0}>
                        {sampleTypeToggle}
                    </Flex>
                )}
                <Box flex={1} minH={0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={simpleRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                            <YAxis
                                tick={{ fontSize: 11, fill: '#718096' }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => formatAxis(v, isCurrency)}
                            />
                            <Tooltip
                                contentStyle={TOOLTIP_STYLE}
                                formatter={(value: number, name: string) => [
                                    formatChartValue(value, isCurrency ? 'currency' : 'number'),
                                    name,
                                ]}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                name="Current"
                                stroke={chartColorAt(0)}
                                strokeWidth={2}
                                dot={false}
                            />
                            {showComparisonBars && (
                                <Line
                                    type="monotone"
                                    dataKey="prior"
                                    name="Prior"
                                    stroke="#A0AEC0"
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    dot={false}
                                />
                            )}
                            {showRefs && typeof data?.targetQuantitySum === 'number' && (
                                <ReferenceLine
                                    y={data.targetQuantitySum}
                                    stroke={chartColorAt(2)}
                                    strokeDasharray="6 4"
                                    label={{ value: 'Target', position: 'insideTopRight', fontSize: 11, fill: chartColorAt(2) }}
                                />
                            )}
                            {showRefs && typeof data?.reorderQuantitySum === 'number' && (
                                <ReferenceLine
                                    y={data.reorderQuantitySum}
                                    stroke={chartColorAt(3)}
                                    strokeDasharray="6 4"
                                    label={{ value: 'Reorder', position: 'insideBottomRight', fontSize: 11, fill: chartColorAt(3) }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        );
    }

    // —— Signed net bars / generic bars ——
    const isSigned = metric === 'netQuantityChange' || metric === 'netTransactionValue';
    return (
        <Box h={height} w="100%" minH={0} display="flex" flexDirection="column">
            {sampleTypeToggle && (
                <Flex justify="flex-end" mb={2} flexShrink={0}>
                    {sampleTypeToggle}
                </Flex>
            )}
            <Box flex={1} minH={0}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={simpleRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                        <YAxis
                            tick={{ fontSize: 11, fill: '#718096' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => formatAxis(v, isCurrency)}
                        />
                        <Tooltip
                            contentStyle={TOOLTIP_STYLE}
                            formatter={(value: number, name: string) => [
                                formatChartValue(value, isCurrency ? 'currency' : 'number'),
                                name,
                            ]}
                        />
                        {(isSigned || simpleRows.some((r) => (r.value ?? 0) < 0 || (r.prior ?? 0) < 0)) && (
                            <ReferenceLine y={0} stroke="#CBD5E0" />
                        )}
                        {showComparisonBars && (
                            <Bar
                                dataKey="prior"
                                name="Prior"
                                fill={PRIOR_COMPARISON_BAR_FILL}
                                fillOpacity={0.9}
                                radius={[4, 4, 0, 0]}
                            />
                        )}
                        <Bar dataKey="value" name="Current" radius={[4, 4, 0, 0]}>
                            {simpleRows.map((row, i) => (
                                <Cell
                                    key={`${row.date}-${i}`}
                                    fill={isSigned ? signedBarColor(row.value) : chartColorAt(0)}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}

export default InventoryTimeseriesChart;
